import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { InvestmentFinancialMetricsDTO, TransactionCategory } from '@inversor-hub/types';
import { transactionCategoryLabels } from '@/features/investment-transactions/constants';
import { formatCurrency } from '@/utils/misc';
import { cardSurface } from '../shared/surfaces';

interface OperatingExpensesBreakdownProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

export const OperatingExpensesBreakdown = ({
  metrics,
  scaleAmount,
}: OperatingExpensesBreakdownProps) => {
  const rows = Object.entries(metrics.operatingExpensesByCategory)
    .map(([category, amount]) => ({
      category,
      label: transactionCategoryLabels[category as TransactionCategory] ?? category,
      amount: scaleAmount(amount),
    }))
    .filter((row) => row.amount > 0)
    .sort((left, right) => right.amount - left.amount);

  const total = rows.reduce((sum, row) => sum + row.amount, 0);

  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            En qué se va el dinero
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gastos operativos pagados en el periodo, por categoría.
          </Typography>
        </Box>
        <Box textAlign={{ xs: 'left', sm: 'right' }}>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1}>
            Total
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(total)}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 2.5 }} />

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay gastos operativos registrados en este periodo.
        </Typography>
      ) : (
        <Stack spacing={2.5}>
          {rows.map((row) => {
            const pct = total > 0 ? (row.amount / total) * 100 : 0;
            return (
              <Box key={row.category}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                  <Typography variant="body2" fontWeight={500}>
                    {row.label}
                  </Typography>
                  <Box display="flex" gap={1.5} alignItems="baseline">
                    <Typography variant="body2" color="text.secondary">
                      {pct.toFixed(1)}%
                    </Typography>
                    <Typography variant="body1" fontWeight={600} minWidth={90} textAlign="right">
                      {formatCurrency(row.amount)}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pct, 100)}
                  color="warning"
                  sx={{ height: 6, borderRadius: 3 }}
                  aria-label={`${row.label}: ${formatCurrency(row.amount)}`}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};
